/**
 * ReadOnly Field Component
 * Display a field value as read-only text
 * Supports custom render function via fieldProps.render
 */
import React from 'react'
import { Typography, Box } from '@mui/material'
import { FormFieldProps } from '../types'
import { useFormContext } from '../FormContext'

const ReadOnlyField: React.FC<FormFieldProps> = ({ config, values }) => {
  const { name, label, fieldProps = {} } = config
  const value = values[name]
  const formContext = useFormContext()
  const dataSources = formContext?.dataSources || {}

  // Handle different value types
  let displayValue = value

  // Process data source options
  if (config.dataSource && value !== undefined && value !== null) {
    const dataSourceKey = config.dataSource
    const dataSourceState = dataSources[dataSourceKey]

    if (dataSourceState?.data) {
      const valueField = config.valueField || 'value'
      const labelField = config.labelField || 'label'

      const item = dataSourceState.data.find(
        (item: any) => item[valueField] === value
      )

      if (item) {
        displayValue = item[labelField] || value
      }
    }
  }
  // Handle options for select-like fields
  else if (config.options && value !== undefined && value !== null) {
    const option = config.options.find(opt => opt.value === value)
    if (option) {
      displayValue = option.label
    }
  }

  // Handle boolean values
  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No'
  }

  // Support custom render function
  const renderContent = () => {
    if (fieldProps.render && typeof fieldProps.render === 'function') {
      return fieldProps.render(value, values)
    }
    return (
      <Typography
        variant='body1'
        sx={{
          wordBreak: 'break-word',
          ...fieldProps.sx
        }}>
        {displayValue || '-'}
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        pt: 0,
        transform: 'translateY(-16px)',
        height: fieldProps.formItemHeight ?? '36px'
      }}>
      <Typography variant='caption' color='textSecondary'>
        {label}
      </Typography>
      {renderContent()}
    </Box>
  )
}

export default ReadOnlyField
